import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePreferences, useUpdatePreferences } from './usePreferences';

/**
 * Phase 31 — Per-project foldable swimlane row state.
 *
 * Reads fold state from the existing `usePreferences` TanStack Query cache (no extra
 * fetch). Writes via `useUpdatePreferences` with a 250ms debounce and a FULL
 * `kanbanFolds` object so concurrent project edits don't wipe each other (Pitfall 3).
 *
 * Semantics (CONTEXT D-06):
 *   - `kanbanFolds[projectId][rowKey] === true` → folded
 *   - absent / `false` → expanded (D-08 default)
 *   - We only ever WRITE `true`. Unfolding deletes the key.
 */
export function useFoldState(projectId: string): {
  isFolded: (rowKey: string) => boolean;
  onToggleFold: (rowKey: string) => void;
} {
  const { data: preferences } = usePreferences();
  const updateMutation = useUpdatePreferences();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // CR-03 fix — accumulator that survives across debounced calls. When the
  // user toggles row A then row B within 250ms, the previous implementation
  // re-read `preferences?.kanbanFolds?.[projectId]` (still the stale cached
  // value, because no PATCH had completed yet) and replaced it with `{ B }`,
  // dropping the A toggle on the floor. With the buffer the second toggle
  // builds on top of `{ A }`, producing `{ A, B }` and a single trailing
  // PATCH. Drained back to null when the timer fires.
  const pendingRef = useRef<Record<string, boolean> | null>(null);

  // Memoize so the useCallback deps array is stable across renders that
  // didn't actually change the fold map (avoids the react-hooks/exhaustive-deps
  // logical-expression warning).
  const folds = useMemo<Record<string, boolean>>(
    () => preferences?.kanbanFolds?.[projectId] ?? {},
    [preferences, projectId],
  );

  const isFolded = useCallback((rowKey: string) => folds[rowKey] === true, [folds]);

  const onToggleFold = useCallback(
    (rowKey: string) => {
      // Prefer the most recent in-flight pending state over the (potentially
      // stale) cached preferences. This is what coalesces multi-row toggles
      // within the 250ms debounce window into a single PATCH.
      const base =
        pendingRef.current ?? preferences?.kanbanFolds?.[projectId] ?? {};
      const next: Record<string, boolean> = { ...base };
      if (next[rowKey] === true) {
        delete next[rowKey]; // unfold = delete (D-06)
      } else {
        next[rowKey] = true; // fold
      }
      pendingRef.current = next;

      // Defer the optimistic cache update to onMutate (inside the debounce
      // timer) so that the snapshot captured by onMutate is the pre-toggle
      // state and rollback on PATCH failure correctly restores it.
      // The 250ms delay before the fold/unfold is visible is an accepted UX
      // trade-off (F-1107); revisit if user testing flags it as disruptive.
      const nextKanbanFolds = {
        ...preferences?.kanbanFolds, // PRESERVE other projects' folds (Pitfall 3)
        [projectId]: next,
      };

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        updateMutation.mutate({
          kanbanFolds: nextKanbanFolds,
        });
        pendingRef.current = null; // accumulator drained on flush
      }, 250);
    },
    [preferences, projectId, updateMutation],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return { isFolded, onToggleFold };
}
